import React, { useState, useEffect, useRef, useCallback } from "react";

function EmailSignup({ id }: { id?: string }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<
    "idle" | "submitting" | "followup" | "submitting-followup" | "success"
  >("idle");
  const [userType, setUserType] = useState<"" | "company" | "engineer">("");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus("submitting");

    if (process.env.REACT_APP_DEV === "true") {
      await new Promise((r) => setTimeout(r, 500));
      setStatus("followup");
      return;
    }

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          "form-name": "waitlist",
          email,
        }).toString(),
      });

      if (response.ok) {
        setStatus("followup");
      } else {
        setStatus("idle");
      }
    } catch {
      setStatus("idle");
    }
  };

  const handleFollowup = async (skip: boolean) => {
    if (skip) {
      setStatus("success");
      return;
    }

    setStatus("submitting-followup");

    if (process.env.REACT_APP_DEV === "true") {
      await new Promise((r) => setTimeout(r, 500));
      setStatus("success");
      return;
    }

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          "form-name": "waitlist-followup",
          email,
          "user-type": userType,
          feedback,
        }).toString(),
      });

      if (response.ok) {
        setStatus("success");
      } else {
        setStatus("success");
      }
    } catch {
      setStatus("success");
    }
  };

  if (status === "success") {
    return (
      <div className="flex items-center gap-3 animate-fade-up">
        <div className="w-6 h-6 rounded-full bg-[#3b9ea3] flex items-center justify-center">
          <svg
            className="w-3 h-3 text-[#0c0d0f]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <span className="font-display text-lg text-white">
          Thank you! We'll reach out when we have updates.
        </span>
      </div>
    );
  }

  if (status === "followup" || status === "submitting-followup") {
    return (
      <div className="w-full max-w-xl space-y-4 animate-fade-up">
        <p className="text-muted text-sm">
          One more thing — help us understand you better (optional)
        </p>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setUserType(userType === "company" ? "" : "company")}
            className={`flex-1 px-4 py-3 rounded-md border text-sm font-medium transition-all ${
              userType === "company"
                ? "border-[#3b9ea3] bg-[#3b9ea3]/20 text-[#3b9ea3]"
                : "bg-[#1a1d24] border-gray-600 text-gray-400 hover:border-gray-500"
            }`}
          >
            I'm hiring
          </button>
          <button
            type="button"
            onClick={() =>
              setUserType(userType === "engineer" ? "" : "engineer")
            }
            className={`flex-1 px-4 py-3 rounded-md border text-sm font-medium transition-all ${
              userType === "engineer"
                ? "border-[#3b9ea3] bg-[#3b9ea3]/20 text-[#3b9ea3]"
                : "bg-[#1a1d24] border-gray-600 text-gray-400 hover:border-gray-500"
            }`}
          >
            I'm an engineer
          </button>
        </div>

        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="What frustrates you about hiring today?"
          rows={3}
          className="input-elegant w-full px-4 py-3 text-sm resize-none"
        />

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => handleFollowup(false)}
            disabled={status === "submitting-followup"}
            className="btn-primary flex-1 px-6 py-3 text-sm disabled:opacity-50"
          >
            {status === "submitting-followup" ? "Submitting..." : "Submit"}
          </button>
          <button
            type="button"
            onClick={() => handleFollowup(true)}
            disabled={status === "submitting-followup"}
            className="px-6 py-3 text-sm text-gray-400 hover:text-white transition-colors"
          >
            Skip
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      id={id}
      name="waitlist"
      data-netlify="true"
      onSubmit={handleSubmit}
      className="signup-form flex flex-col sm:flex-row gap-3 w-full max-w-xl scroll-mt-28"
    >
      <input type="hidden" name="form-name" value="waitlist" />
      <div className="relative flex-1">
        <svg
          className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted pointer-events-none"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
        <input
          id={id ? `${id}-email` : undefined}
          type="email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          required
          className="input-elegant w-full pl-12 pr-5 py-4 text-base"
        />
      </div>
      <button
        type="submit"
        disabled={status === "submitting"}
        className="btn-primary group px-8 py-4 text-base disabled:opacity-50"
      >
        <span className="flex items-center justify-center gap-2">
          {status === "submitting" ? (
            <>
              <svg
                className="w-4 h-4 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              Joining...
            </>
          ) : (
            <>
              Join the waitlist
              <span
                aria-hidden="true"
                className="inline-block transition-transform duration-200 group-hover:translate-x-1"
              >
                {"\u2192"}
              </span>
            </>
          )}
        </span>
      </button>
    </form>
  );
}

// Teal/slate palette - tech-forward
const PARTICLE_COLORS = [
  { r: 59, g: 158, b: 163 }, // accent teal
  { r: 82, g: 182, b: 187 }, // light teal
  { r: 156, g: 163, b: 175 }, // slate
  { r: 107, g: 114, b: 128 }, // muted slate
];

// Particle class for the "Constellation Ascension" background
class Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  baseRadius: number;
  baseAlpha: number;
  color: { r: number; g: number; b: number };
  phase: number; // For twinkling effect
  isParagon: boolean; // Is this the "star" of its cluster?

  constructor(
    canvasWidth: number,
    canvasHeight: number,
    color: { r: number; g: number; b: number },
    isParagon: boolean
  ) {
    this.x = Math.random() * canvasWidth;
    this.y = Math.random() * canvasHeight;
    // Horizontal drift is gentle, vertical is always upward (negative)
    this.vx = (Math.random() - 0.5) * 0.2;
    this.vy = -Math.random() * 0.15 - 0.05; // Always drifting upward (ascension)
    this.phase = Math.random() * Math.PI * 2; // Random phase for twinkling
    this.color = color;
    this.isParagon = isParagon;

    // Paragon particles are larger and brighter
    if (isParagon) {
      this.baseRadius = Math.random() * 0.7 + 2.5; // 2.5 - 3.2
      this.baseAlpha = Math.random() * 0.2 + 0.75; // 0.75 - 0.95
    } else {
      this.baseRadius = Math.random() * 1.2 + 0.3; // 0.3 - 1.5
      this.baseAlpha = Math.random() * 0.4 + 0.1; // 0.1 - 0.5
    }
    this.radius = this.baseRadius;
  }

  update(
    canvasWidth: number,
    canvasHeight: number,
    mouseX: number,
    mouseY: number,
    time: number
  ) {
    // Move particle with upward drift
    this.x += this.vx;
    this.y += this.vy;

    // Twinkle effect - radius varies with time
    this.radius = this.baseRadius + Math.sin(time * 0.002 + this.phase) * 0.3;

    // Respawn at bottom when reaching top (ascension cycle)
    if (this.y < -10) {
      this.y = canvasHeight + 10;
      this.x = Math.random() * canvasWidth;
    }

    // Horizontal wrap
    if (this.x < 0) this.x = canvasWidth;
    if (this.x > canvasWidth) this.x = 0;

    // Mouse interaction - gentle repulsion (particles are aspirational, not needy)
    const dx = mouseX - this.x;
    const dy = mouseY - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const maxDist = 150;

    if (dist < maxDist && dist > 0) {
      const force = ((maxDist - dist) / maxDist) * 0.008;
      this.vx -= (dx / dist) * force; // Repel, don't attract
      this.vy -= (dy / dist) * force;
    }

    // Dampen horizontal velocity
    this.vx *= 0.995;
    // Maintain upward momentum
    this.vy = Math.max(this.vy, -0.25);
    if (this.vy > -0.05) {
      this.vy = -0.05 - Math.random() * 0.1;
    }
  }

  draw(
    ctx: CanvasRenderingContext2D,
    mouseX: number,
    mouseY: number,
    ambientX: number,
    ambientY: number
  ) {
    // Calculate mouse glow intensity
    const mouseDx = mouseX - this.x;
    const mouseDy = mouseY - this.y;
    const mouseDist = Math.sqrt(mouseDx * mouseDx + mouseDy * mouseDy);
    const mouseMaxDist = 180;
    let mouseIntensity = 0;
    if (mouseDist < mouseMaxDist) {
      mouseIntensity = 1 - mouseDist / mouseMaxDist;
    }

    // Calculate ambient light glow intensity (slightly larger range)
    const ambientDx = ambientX - this.x;
    const ambientDy = ambientY - this.y;
    const ambientDist = Math.sqrt(
      ambientDx * ambientDx + ambientDy * ambientDy
    );
    const ambientMaxDist = 220;
    const ambientStrength = 0.85; // 85% of mouse intensity
    let ambientIntensity = 0;
    if (ambientDist < ambientMaxDist) {
      ambientIntensity = (1 - ambientDist / ambientMaxDist) * ambientStrength;
    }

    // Combine intensities (use max to prevent over-saturation)
    let intensity = Math.max(mouseIntensity, ambientIntensity);

    // Paragon particles always have a subtle glow and enhanced effects when illuminated
    const glowMultiplier = this.isParagon ? 6 : 4;
    const glowAlphaMultiplier = this.isParagon ? 0.35 : 0.2;
    const baseGlow = 0; // Only glow when spotlight passes over

    let alpha = this.baseAlpha;
    let radius = this.radius;

    // Effective intensity includes base glow for paragons
    const effectiveIntensity = Math.max(intensity, baseGlow);

    if (effectiveIntensity > 0) {
      alpha = this.baseAlpha + effectiveIntensity * 0.4;
      radius = this.radius + effectiveIntensity * (this.isParagon ? 1.5 : 1.2);

      // Draw glow
      const gradient = ctx.createRadialGradient(
        this.x,
        this.y,
        0,
        this.x,
        this.y,
        radius * glowMultiplier
      );
      gradient.addColorStop(
        0,
        `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${
          effectiveIntensity * glowAlphaMultiplier
        })`
      );
      gradient.addColorStop(
        1,
        `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, 0)`
      );
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(this.x, this.y, radius * glowMultiplier, 0, Math.PI * 2);
      ctx.fill();
    }

    // Draw particle with star-like quality
    ctx.fillStyle = `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, ${alpha})`;
    ctx.beginPath();
    ctx.arc(this.x, this.y, radius, 0, Math.PI * 2);
    ctx.fill();
  }
}

// Helper to pick a random spot along a random edge
const pickRandomEdgeTarget = (
  light: { targetX: number; targetY: number },
  width: number,
  height: number
) => {
  const edge = Math.floor(Math.random() * 4); // 0=top, 1=right, 2=bottom, 3=left
  const margin = 100;

  switch (edge) {
    case 0: // Top
      light.targetX = margin + Math.random() * (width - margin * 2);
      light.targetY = margin;
      break;
    case 1: // Right
      light.targetX = width - margin;
      light.targetY = margin + Math.random() * (height - margin * 2);
      break;
    case 2: // Bottom
      light.targetX = margin + Math.random() * (width - margin * 2);
      light.targetY = height - margin;
      break;
    case 3: // Left
      light.targetX = margin;
      light.targetY = margin + Math.random() * (height - margin * 2);
      break;
  }
};

function InteractiveParticleBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const ambientLightRef = useRef({
    x: 0,
    y: 0,
    targetX: 0,
    targetY: 0,
  });
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>();
  const timeRef = useRef<number>(0);

  const initParticles = useCallback((width: number, height: number) => {
    // Increased particle count for denser star field
    const particleCount = Math.min(240, Math.floor((width * height) / 6000));
    const PARAGON_COUNT = 16; // Fixed number of paragon particles
    const PARAGON_COLOR = { r: 255, g: 200, b: 100 }; // Bright vibrant gold
    const particles: Particle[] = [];

    for (let i = 0; i < particleCount; i++) {
      const isParagon = i < PARAGON_COUNT;
      const colorIndex = i % PARTICLE_COLORS.length;
      const color = isParagon ? PARAGON_COLOR : PARTICLE_COLORS[colorIndex];
      particles.push(new Particle(width, height, color, isParagon));
    }

    particlesRef.current = particles;
  }, []);

  const updateAmbientLight = useCallback((width: number, height: number) => {
    const light = ambientLightRef.current;

    // Initialize if needed
    if (light.x === 0 && light.y === 0) {
      light.x = width * 0.5;
      light.y = height * 0.5;
      pickRandomEdgeTarget(light, width, height);
    }

    // Check if reached target
    const dx = light.targetX - light.x;
    const dy = light.targetY - light.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < 50) {
      pickRandomEdgeTarget(light, width, height);
    }

    // Move toward target at constant speed
    const speed = 1.8;
    if (dist > speed) {
      light.x += (dx / dist) * speed;
      light.y += (dy / dist) * speed;
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set up canvas size
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      if (particlesRef.current.length === 0) {
        initParticles(canvas.width, canvas.height);
      }
    };
    resize();
    window.addEventListener("resize", resize);

    // Mouse tracking
    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener("mousemove", handleMouseMove);

    // Mouse leave - reset position
    const handleMouseLeave = () => {
      mouseRef.current = { x: -1000, y: -1000 };
    };
    document.addEventListener("mouseleave", handleMouseLeave);

    // Draw spotlight glow (before particles so it's underneath)
    const drawSpotlight = (x: number, y: number) => {
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, 280);
      gradient.addColorStop(0, "rgba(59, 158, 163, 0.08)");
      gradient.addColorStop(0.5, "rgba(59, 158, 163, 0.03)");
      gradient.addColorStop(1, "rgba(59, 158, 163, 0)");

      ctx.beginPath();
      ctx.arc(x, y, 280, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();
    };

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      timeRef.current = performance.now();

      const mouse = mouseRef.current;
      const time = timeRef.current;

      // Update ambient light position
      updateAmbientLight(canvas.width, canvas.height);
      const ambient = ambientLightRef.current;

      // Draw spotlight glow
      drawSpotlight(ambient.x, ambient.y);

      // Update and draw particles
      particlesRef.current.forEach((p) => {
        p.update(canvas.width, canvas.height, mouse.x, mouse.y, time);
        p.draw(ctx, mouse.x, mouse.y, ambient.x, ambient.y);
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseleave", handleMouseLeave);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [initParticles, updateAmbientLight]);

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {/* Teal gradient blobs layer (underneath) */}
      <div
        className="absolute -top-[400px] -right-[200px] w-[800px] h-[800px] rounded-full opacity-15 animate-blob"
        style={{
          background:
            "radial-gradient(circle, rgba(59, 158, 163, 0.2) 0%, transparent 70%)",
          filter: "blur(100px)",
        }}
      />
      <div
        className="absolute -bottom-[300px] -left-[200px] w-[700px] h-[700px] rounded-full opacity-12 animate-blob-reverse animation-delay-2000"
        style={{
          background:
            "radial-gradient(circle, rgba(82, 182, 187, 0.15) 0%, transparent 70%)",
          filter: "blur(100px)",
        }}
      />
      <div
        className="absolute top-[30%] left-[40%] w-[600px] h-[600px] rounded-full opacity-8 animate-blob animation-delay-4000"
        style={{
          background:
            "radial-gradient(circle, rgba(156, 163, 175, 0.12) 0%, transparent 70%)",
          filter: "blur(120px)",
        }}
      />

      {/* Interactive particle canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-10"
        style={{ pointerEvents: "none" }}
      />

      {/* Grid overlay */}
      <div className="absolute inset-0 bg-grid-pattern animate-grid-pulse z-20" />

      {/* Noise texture */}
      <div className="absolute inset-0 noise-texture z-20" />

      {/* Vignette effect - deeper for more dramatic feel */}
      <div
        className="absolute inset-0 z-20"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 0%, rgba(8, 8, 10, 0.6) 100%)",
        }}
      />
    </div>
  );
}

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return { ref, isVisible };
}

function RevealSection({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, isVisible } = useScrollReveal();

  return (
    <div
      ref={ref}
      className={`reveal ${isVisible ? "visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

const companyHighlights = [
  {
    title: "Set up your company profile and define the role",
    desc: "Tell us about the work, the team, and what success looks like beyond a job description.",
  },
  {
    title: "Review vetted engineers",
    desc: "See candidates who have demonstrated how they work, how they learn, and who they are, not just what's on a resum\u00e9.",
  },
  {
    title: "Hire with confidence",
    desc: "Move quickly knowing every intro is with a high quality candidate.",
  },
];

const engineerHighlights = [
  {
    title: "Create a profile that shows who you are",
    desc: "Go beyond a resum\u00e9 with a technical vetting exercise, behavioral questions, and personality video prompts that show how you think and work.",
  },
  {
    title: "Get intentional introductions",
    desc: "We only show your profile to vetted companies, and you only hear from teams whose expectations align with yours.",
  },
  {
    title: "Interview with clarity",
    desc: "Every conversation starts with mutual interest and context, not cold outreach or keyword filters.",
  },
];

function App() {
  return (
    <div className="min-h-screen bg-primary text-white relative">
      <InteractiveParticleBackground />

      {/* Navigation */}
      <nav className="w-full py-6 sticky top-0 bg-[#0c0d0f]/80 backdrop-blur-xl z-50 border-b border-[#3b9ea3]/10">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 flex items-center justify-between">
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">
            <span className="text-gradient">Paragon</span>
          </h1>
          <button
            onClick={() => {
              const input = document.getElementById("signup-email");
              if (input) {
                input.scrollIntoView({ behavior: "smooth", block: "center" });
                setTimeout(() => (input as HTMLInputElement).focus(), 400);
              }
            }}
            className="btn-primary px-5 py-2.5 text-sm"
          >
            <span>Join the waitlist</span>
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative">
        {/* Hero Section */}
        <section className="relative pt-16 pb-20 md:pt-24 md:pb-20 overflow-hidden">
          <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-4xl">
              {/* Hero headline - each key phrase on its own line */}
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-semibold leading-tight tracking-tight space-y-1">
                <span className="block animate-fade-up">
                  Where{" "}
                  <span className="inline-block text-gradient">
                    great teams
                  </span>
                </span>
                <span className="block animate-fade-up delay-150">
                  hire{" "}
                  <span className="inline-block text-gradient">
                    great engineers
                  </span>
                </span>
              </h2>

              <div className="mt-8 max-w-4xl space-y-6 animate-fade-up delay-400">
                <p className="text-xl md:text-2xl text-secondary font-light leading-relaxed">
                  Hiring is broken. We're fixing it.
                </p>

                <div className="card-gradient-border p-6 sm:p-8 shadow-[0_20px_60px_-45px_rgba(229,161,88,0.4)]">
                  <div className="grid gap-6 md:gap-8 md:grid-cols-2 text-base md:text-lg text-secondary leading-relaxed">
                    <div className="relative pl-5 border-l-2 border-[#3b9ea3]/60">
                      <p className="uppercase tracking-[0.25em] text-xs text-[#3b9ea3] font-medium mb-2">
                        For teams
                      </p>
                      <p>
                        Hire engineers{" "}
                        <span className="font-semibold text-white">
                          with confidence, clarity, and efficiency
                        </span>
                        .
                      </p>
                    </div>
                    <div className="relative pl-5 border-l-2 border-[#e5a158]/60">
                      <p className="uppercase tracking-[0.25em] text-xs text-[#e5a158] font-medium mb-2">
                        For engineers
                      </p>
                      <p>
                        Get hired{" "}
                        <span className="font-semibold text-white">
                          through intentional introductions, not mass
                          applications
                        </span>
                        .
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-12 animate-fade-up delay-500">
                <EmailSignup id="signup" />
              </div>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="divider-gradient" />
        </div>

        {/* How It Works */}
        <section className="py-24 md:py-32 relative">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <RevealSection>
              <h3 className="font-display text-4xl md:text-5xl font-bold text-center mb-20">
                How it works
              </h3>
            </RevealSection>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
              {/* For Companies */}
              <RevealSection delay={100}>
                <div>
                  <h4 className="font-display text-2xl font-semibold mb-10">
                    <span className="text-[#3b9ea3]">For Companies</span>
                  </h4>
                  <div className="relative">
                    {/* Timeline connector */}
                    <div className="absolute left-[22px] top-8 bottom-8 w-px bg-gradient-to-b from-[#3b9ea3] via-[#3b9ea3]/30 to-transparent" />

                    <div className="space-y-10">
                      {companyHighlights.map((step, index) => (
                        <div
                          key={step.title}
                          className="flex gap-6 group relative"
                        >
                          <div className="flex-shrink-0 relative z-10">
                            {/* Number circle */}
                            <div className="w-11 h-11 rounded-full bg-[#0c0d0f] border-2 border-[#3b9ea3]/40 flex items-center justify-center group-hover:border-[#3b9ea3] group-hover:shadow-[0_0_20px_rgba(59,158,163,0.3)] transition-all duration-300">
                              <span className="font-display text-lg font-semibold text-[#3b9ea3]">
                                {index + 1}
                              </span>
                            </div>
                          </div>
                          <div className="pt-2">
                            <h5 className="font-display text-xl font-semibold mb-2 group-hover:text-white transition-colors">
                              {step.title}
                            </h5>
                            <p className="text-secondary leading-relaxed">
                              {step.desc}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </RevealSection>

              {/* For Engineers */}
              <RevealSection delay={200}>
                <div>
                  <h4 className="font-display text-2xl font-semibold mb-10">
                    <span className="text-[#e5a158]">For Engineers</span>
                  </h4>
                  <div className="relative">
                    {/* Timeline connector */}
                    <div className="absolute left-[22px] top-8 bottom-8 w-px bg-gradient-to-b from-[#e5a158] via-[#e5a158]/30 to-transparent" />

                    <div className="space-y-10">
                      {engineerHighlights.map((step, index) => (
                        <div
                          key={step.title}
                          className="flex gap-6 group relative"
                        >
                          <div className="flex-shrink-0 relative z-10">
                            {/* Number circle */}
                            <div className="w-11 h-11 rounded-full bg-[#0c0d0f] border-2 border-[#e5a158]/40 flex items-center justify-center group-hover:border-[#e5a158] group-hover:shadow-[0_0_20px_rgba(229,161,88,0.3)] transition-all duration-300">
                              <span className="font-display text-lg font-semibold text-[#e5a158]">
                                {index + 1}
                              </span>
                            </div>
                          </div>
                          <div className="pt-2">
                            <h5 className="font-display text-xl font-semibold mb-2 group-hover:text-white transition-colors">
                              {step.title}
                            </h5>
                            <p className="text-secondary leading-relaxed">
                              {step.desc}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </RevealSection>
            </div>
          </div>
        </section>

        {/* Why Trust Paragon */}
        <section className="py-24 md:py-32 relative">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <RevealSection>
              <h3 className="font-display text-4xl md:text-5xl font-semibold mb-6">
                Why trust Paragon?
              </h3>
              <div className="space-y-6 text-xl text-secondary max-w-3xl mx-auto">
                <p>
                  Paragon is built by software engineers with 10+ years in the
                  industry who have lived through broken hiring processes on
                  both sides of the table.
                </p>
                <p>
                  We've been there, and we're building the hiring experience we
                  always wished existed.
                </p>
              </div>
            </RevealSection>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-28 md:py-36 relative overflow-hidden">
          <div className="relative max-w-6xl mx-auto px-6 lg:px-8 text-center">
            <RevealSection>
              <h3 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold mb-6">
                Join the waitlist
              </h3>
              <div className="text-xl text-secondary mb-14 space-y-6 max-w-2xl mx-auto">
                <p>
                  We're still under development, but if you'd like to learn more
                  as we move toward launch, enter your email below.
                </p>
              </div>
              <div className="flex justify-center">
                <EmailSignup />
              </div>
            </RevealSection>
          </div>
        </section>
      </main>
    </div>
  );
}

export default App;
